import '../../platform.css';
import './ownTeam.css';

import { useState, useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { teamsState } from '../../recoil/atoms/teamsState';
import { playersState } from '../../recoil/atoms/playersState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { navigationPropsState } from '../../recoil/atoms/navigationPropsState';
import { useSetNavigationProps } from '../../recoil/hooks/useSetNavigationProps';

import GroupsIcon from '@mui/icons-material/Groups';
import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import { getSortDistanceRole } from '../../utils/playerUtils';
import { OwnTeamMatrixView } from './OwnTeamMatrixView';
import { OwnTeamTableView } from './OwnTeamTableView';
import { LogisticTeamView } from '../../components/teamView/LogisticTeamView';
import { getEuroToCurrency } from '../../utils/economyUtils';
import { FixedTabs } from '../../components/controls/navigation/FixedTabs';
import { StringToAnyMap } from '../../types/common';
import { getAge } from '../../utils/ageUtils';
import { PlayerMap } from '../../types/player';


export const OwnTeam = () => {

  const tenantSettings = useRecoilValue(tenantSettingsState);
  const teams = useRecoilValue(teamsState);

  const players = useRecoilValue(playersState);
  const playerOverviews = useRecoilValue(playerOverviewsState);

  const navigationProps = useRecoilValue(navigationPropsState);
  const activeSubTab = navigationProps.activeOwnTeamSubTab ?? 0;
  const updateNavigationProps = useSetNavigationProps();
  const setActiveSubTab = (subTab: number) => {
    updateNavigationProps({ activeOwnTeamSubTab: subTab }, true);
  };

  const [showFirstEleven, setShowFirstEleven] = useState(false);

  const tabOptions = [
    'playerSquad',
    'table',
    'matrix',
  ];

  const tabIcons = [
    <GroupsIcon key={'groups-icon'} style={{ fontSize: 21 }} />,
    <FormatListBulletedIcon key={'format-list-icon'} style={{ fontSize: 21 }} />,
    <WindowOutlinedIcon key={'window-icon'} style={{ fontSize: 21 }} />,
  ];

  const [logisticTeamViewToggle, setLogisticTeamViewToggle] = useState(false);


  const addPlayerOverviewData = (player: StringToAnyMap, clubCountryCode: string) => {
    const numberId = Number(player.id);
    if (!isNaN(numberId) && playerOverviews[numberId]) {
      player['image_url'] = playerOverviews[numberId].image_url;
      player['skill_index'] = playerOverviews[numberId].position_group_stats?.OVERALL?.skill_index?.index;
      player['physical_index'] = playerOverviews[numberId].position_group_stats?.OVERALL?.physical_index?.index;

      // values to be used, where user provided values have presedence
      player['birth_date'] = playerOverviews[numberId].birth_date;
      player['country_code'] = playerOverviews[numberId].country_code;
      player['contract_expiration'] = playerOverviews[numberId].contract_expiration;
      player['market_value'] = getEuroToCurrency(playerOverviews[numberId].market_value, clubCountryCode);

      // values that we have in our database
      player['birth_date_in_database'] = playerOverviews[numberId].birth_date;
      player['country_code_in_database'] = playerOverviews[numberId].country_code;
      player['contract_expiration_in_database'] = playerOverviews[numberId].contract_expiration;
      player['market_value_in_database'] = getEuroToCurrency(playerOverviews[numberId].market_value, clubCountryCode);
    }
  };


  const addPlayerData = (player: StringToAnyMap) => {
    if (player.id in players) {
      const playerDocument = players[player.id];
      Object.entries(playerDocument).forEach(([property, value]) => {
        if (value !== undefined && value !== null) {
          player[property] = value;
        }
      });
      if (playerDocument.salary || playerDocument.bonuses) {
        player['salaryPlusBonuses'] = (playerDocument.salary ?? 0) + (playerDocument.bonuses ?? 0);
      }
    }
  };


  // ownTeamConfig = {
  //     'ownTeamPlayers': map from playerId to data for all players in ownTeam,
  //     'bench': list of playerData for all players on ownTeam who are not in the starting eleven,
  // }
  const [ownTeamConfig, setOwnTeamConfig] = useState<StringToAnyMap | undefined>(undefined);

  useEffect(() => {
    if (!teams || !players || !playerOverviews) return;

    const ownTeam = teams['ownTeam'];
    const ownTeamPlayers: PlayerMap = {};
    const bench: StringToAnyMap[] = [];

    Object.keys(ownTeam.players).forEach(position => {
      ownTeam.players[position].forEach(playerEntry => {

        const player: StringToAnyMap = { ...playerEntry };

        addPlayerOverviewData(player, tenantSettings?.countryCode ?? 'NO');
        addPlayerData(player);

        player['age'] = getAge(player['birth_date']);

        ownTeamPlayers[player.id] = player;

        if (!player.isStartingEleven) {
          bench.push(player);
        }
      });
    });

    bench.sort((a: StringToAnyMap, b: StringToAnyMap) => getSortDistanceRole(a, b, 'role'));

    const newOwnTeamConfig = {
      'ownTeamPlayers': ownTeamPlayers,
      'bench': bench,
    };

    setOwnTeamConfig(newOwnTeamConfig);

  }, [teams, players, playerOverviews, tenantSettings?.countryCode]); // eslint-disable-line react-hooks/exhaustive-deps


  const setTabAndResetExpansions = (index: number) => {
    setActiveSubTab(index);
    setLogisticTeamViewToggle(!logisticTeamViewToggle);
    setShowFirstEleven(false);
  };


  return (
    <div className='platform-view-section'>

      <FixedTabs
        tabOptions={tabOptions}
        activeTab={activeSubTab}
        setActiveTab={setTabAndResetExpansions}
        parentTab={'ownTeam'}
        tabIcons={tabIcons}
      />

      <div className='platform-view-content-container-with-tabs'>
        {teams && activeSubTab === 0 && ownTeamConfig && (
          <LogisticTeamView
            teamId={'ownTeam'}
            team={teams['ownTeam']}
            isTabLine={true}
            showFirstEleven={showFirstEleven}
            setShowFirstEleven={setShowFirstEleven}
            ownTeamPlayers={ownTeamConfig['ownTeamPlayers']}
            benchPlayers={ownTeamConfig['bench']}
          />
        )}

        {activeSubTab === 1 && ownTeamConfig && (
          <OwnTeamTableView
            ownTeamPlayers={ownTeamConfig['ownTeamPlayers']}
          />
        )}

        {activeSubTab === 2 && ownTeamConfig && (
          <OwnTeamMatrixView
            ownTeamPlayers={ownTeamConfig['ownTeamPlayers']}
          />
        )}
      </div>

    </div>
  );
};
